import React from "react";
import { Badge } from "../../../ui/Badge";
import { Button } from "../../../ui/Button";
import { Skeleton } from "../../../ui/Skeleton";
import { diffWords } from "../utils/diff";
import type { Evaluator } from "../types";

interface VersionHistoryProps {
  /** Newest first, including soft-deleted versions. */
  versions: Evaluator[];
  isLoading: boolean;
  error: string | null;
  /** Loads an older version's wording back into the editor. */
  onCopyToEditor: (criteria: string) => void;
}

function formatStamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Colour the runs so an edit reads at a glance. */
function DiffText({ before, after }: { before: string; after: string }) {
  return (
    <p className="font-mono text-xs leading-relaxed break-words">
      {diffWords(before, after).map((token, i) => {
        if (token.op === "same") {
          return (
            <span key={i} className="text-muted-foreground">
              {token.text}{" "}
            </span>
          );
        }
        if (token.op === "added") {
          return (
            <span key={i} className="rounded bg-success/15 px-0.5 text-success">
              {token.text}{" "}
            </span>
          );
        }
        return (
          <span
            key={i}
            className="rounded bg-destructive/15 px-0.5 text-destructive line-through"
          >
            {token.text}{" "}
          </span>
        );
      })}
    </p>
  );
}

/**
 * The edit history of one evaluator.
 *
 * Updating a criterion is append-only underneath — the old row is soft-deleted and a new
 * one inserted under the same group_id — but the UI presented it as a plain save. An edit
 * therefore redefined, without a trace, whatever metric Insights had been charting under
 * that name. Showing the chain with a word-level diff makes that legible.
 *
 * There is still no Restore button, but the reason it was originally left out no longer
 * holds. That reason was `evaluators_criteria_domain_unique`, a non-partial
 * UNIQUE (criteria, domain) that also covered the soft-deleted row holding the old text,
 * so re-inserting it collided and rolled back with a 409. Migration 0056 replaced it with
 * a partial index scoped to live rows, and restoring an old version is now an ordinary
 * write — `test_an_edit_can_be_reverted_to_previous_criteria` covers exactly that.
 *
 * So this is now a product decision rather than a database limit. Copying the wording into
 * the editor still works and keeps the group's history linear; a real Restore button would
 * need a view on what it means for the evaluations already graded under the newer version.
 */
export function VersionHistory({
  versions,
  isLoading,
  error,
  onCopyToEditor,
}: VersionHistoryProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton variant="text" height={14} width={160} />
        <Skeleton height={64} className="rounded-md" />
        <Skeleton height={64} className="rounded-md" />
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-destructive">{error}</p>;
  }

  if (versions.length === 0) {
    return null;
  }

  if (versions.length === 1) {
    return (
      <p className="text-sm text-muted-foreground">
        No edits yet — this is the original criterion, created{" "}
        {formatStamp(versions[0].created_at)}.
      </p>
    );
  }

  return (
    <ol className="flex flex-col gap-3">
      {versions.map((version, index) => {
        // Oldest is version 1; the list arrives newest first.
        const number = versions.length - index;
        const previous = versions[index + 1];
        const isCurrent = !version.deleted;

        return (
          <li
            key={version.id}
            className="rounded-md border border-border bg-card p-3 flex flex-col gap-2"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">
                  Version {number}
                </span>
                {isCurrent && <Badge variant="success">Current</Badge>}
                <span className="text-xs text-muted-foreground">
                  {formatStamp(version.created_at)}
                </span>
              </div>
              {!isCurrent && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onCopyToEditor(version.criteria)}
                >
                  Copy to editor
                </Button>
              )}
            </div>

            {previous ? (
              <DiffText before={previous.criteria} after={version.criteria} />
            ) : (
              <p className="font-mono text-xs leading-relaxed text-muted-foreground break-words">
                {version.criteria}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
}
